import { Box, Flex, SimpleGrid, Skeleton } from "@chakra-ui/react";
import { debounce } from "lodash";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContractStateDto, ContractTaskStatus, PersonDto, useListContractsQuery } from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { MultiSelector } from "../../../../common/input/Selector/MultiSelector";
import { PersonsSelector } from "../../../../common/persons/PersonsSelector";
import { MultiSelectFilter, MultiSelectOption } from "../../view-all/filtering/MultiSelectFilter";

interface Props {
  selectedResponsiblePersons: PersonDto[];
  onSelectedResponsiblePersonsChange: (persons: PersonDto[]) => void;
  selectedContracts: MultiSelectOption[];
  onSelectedContractsChange: (values: MultiSelectOption[]) => void;
  selectedStatuses: MultiSelectOption[];
  onSelectedStatusesChange: (values: MultiSelectOption[]) => void;
}

const statuses = [ContractTaskStatus.Open, ContractTaskStatus.Completed];

export const TaskFilters = ({
  selectedResponsiblePersons,
  onSelectedResponsiblePersonsChange,
  selectedContracts,
  onSelectedContractsChange,
  selectedStatuses,
  onSelectedStatusesChange,
}: Props) => {
  const { t } = useTranslation();
  const authState = useLoggedInWithOrgContextState();

  const [searchName, setSearchName] = useState("");
  const [debouncedSearchName, setDebouncedSearchName] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedSearchName = useCallback(
    debounce((name: string) => setDebouncedSearchName(name), 300),
    []
  );

  const { data, isLoading } = useListContractsQuery({
    orgId: authState.selectedOrg.id,
    state: ContractStateDto.Published,
    title: debouncedSearchName ? debouncedSearchName : undefined,
    limit: 10,
  });

  return (
    <Flex w="full">
      {data && (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing="2" w="full">
          <Box>
            <PersonsSelector
              placeholder={t("Responsible") ?? ""}
              placeholderColor="smMuted"
              organizationIds={[authState.selectedOrg.id]}
              selectedPersons={selectedResponsiblePersons}
              onChange={(values) => onSelectedResponsiblePersonsChange(values)}
            />
          </Box>
          <Box>
            <MultiSelector
              size="sm"
              placeholder={{ text: t("Contract"), color: "smMuted" }}
              inputValue={searchName}
              value={selectedContracts}
              options={
                data?.contracts.map((e) => ({
                  label: e.title,
                  value: e.id,
                })) ?? []
              }
              noOptionsAvailableMessage={t("No contracts available")}
              onInputChange={(name: string) => {
                setSearchName(name);
                handleDebouncedSearchName(name);
              }}
              onChange={(values) => onSelectedContractsChange(values)}
            />
          </Box>
          <Box>
            <MultiSelectFilter
              placeholder={t("Status")}
              options={statuses.map((status) => ({
                label: t(status),
                value: status,
              }))}
              value={selectedStatuses}
              onChange={(values) => onSelectedStatusesChange(values)}
            />
          </Box>
        </SimpleGrid>
      )}
      {isLoading && !data && (
        <Skeleton
          height="8"
          width="full"
          rounded="md"
          startColor="smBackgroundSecondary"
          endColor="smBackgroundTertiary"
        />
      )}
    </Flex>
  );
};
